import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/ui/states';
import { FISCAL_ROUTES, apiMessage, type FiscalCredentialSummary, type FiscalProfile } from '../fiscalApi';

interface Props {
  profile: FiscalProfile | null;
  onClose: () => void;
}

/**
 * `PUT /fiscal/profiles/:id/credentials` — write-only (`docs/fiscal.md`
 * step 3). Nothing this dialog reads ever contains the secret: the list below
 * shows only a kind and the last four characters, which is enough to
 * recognise a value and not enough to use it, and the input clears itself the
 * instant a save succeeds rather than holding the plaintext in state.
 */
export default function CredentialsDialog({ profile, onClose }: Props) {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const [kind, setKind] = useState('CLIENT_ID');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const credentials = useApi<unknown>(profile ? FISCAL_ROUTES.credentials(profile.id) : null);
  const { rows } = rowsOf<FiscalCredentialSummary>(credentials.data);

  if (!profile) return null;

  const submit = async () => {
    if (!value.trim()) return;
    setBusy(true);
    try {
      await api.put(FISCAL_ROUTES.credentials(profile.id), { kind, value: value.trim() });
      toast.success(t('fiscal.credentials.saved'));
      setValue('');
      credentials.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (credKind: string) => {
    const ok = await confirm({
      title: t('fiscal.credentials.removeTitle', { kind: credKind }),
      body: t('fiscal.credentials.removeBody'),
      confirmLabel: t('common.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(FISCAL_ROUTES.credential(profile.id, credKind));
      toast.success(t('fiscal.credentials.removed'));
      credentials.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('fiscal.credentials.title', { name: profile.legalName })}
      description={t('fiscal.credentials.description')}
      className="max-w-lg"
      footer={
        <Button variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <div className="space-y-4">
        {credentials.loading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('fiscal.credentials.none')}</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((c) => (
              <li key={c.kind} className="flex items-center justify-between rounded-lg border p-2 text-sm">
                <div>
                  <p className="font-medium">{c.kind}</p>
                  <p className="font-mono text-xs text-ink-muted">…{c.hint}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(c.kind)} disabled={busy}>
                  <Icon as={Trash2} size="sm" className="text-red-600 dark:text-red-400" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-3 border-t pt-4">
          <Field label={t('fiscal.credentials.kind')} controlId="fiscal-cred-kind">
            <Select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="CLIENT_ID">CLIENT_ID</option>
              <option value="CLIENT_SECRET">CLIENT_SECRET</option>
            </Select>
          </Field>
          <Field label={t('fiscal.credentials.value')} controlId="fiscal-cred-value">
            <Input
              type="password"
              autoComplete="off"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t('fiscal.credentials.valuePlaceholder')}
            />
          </Field>
          <Button size="sm" onClick={submit} disabled={busy || !value.trim()}>
            {t('fiscal.credentials.set')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
